import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_URI } from "../../constants/GlobalConstants";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardContent, Grid, Alert, Button, Typography, Divider } from "@mui/material";
import Loader from "../../components/layout/Loader";
import { fetchInvoiceData } from "../../redux/actions/InvoiceActions";
import { useSnackbarNotification } from "../../components/order/reusables/UseSnackBarNotification";
import { Country, State } from "country-state-city";
import InvoiceHeader from "./InvoiceDetails/InvoiceHeader";
import BuyerInfoForm from "./InvoiceDetails/BuyersInfoForm";
import InvoiceDetailsForm from "./InvoiceDetails/InvoiceDetailsForm";
import ActionButtons from "./InvoiceDetails/ActionButtons";
import InvoicePreview from "./InvoiceDetails/InvoicePreview.";
import InvoiceOrderTable from "./InvoiceOrderTable";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import InvoiceConvConfirmModal from "./InvoiceConvConfirmModal";
import SaveAsPaidModal from "../invoiceListing/SaveAsPaidModal";
import SuccessModal from "./SuccessModal";

const getCountries = () => {
  return Country.getAllCountries()
    .filter((country) => ["CA", "US", "MX"].includes(country.isoCode))
    .map((country) => ({
      name: country.name,
      isoCode: country.isoCode,
    }));
};

const getProvincesByCountry = (countryIsoCode) => {
  return State.getStatesOfCountry(countryIsoCode).map((state) => ({
    name: state.name,
    isoCode: state.isoCode,
  }));
};

const findCountryOption = (value, countries) => {
  if (!value) return null;
  let country = countries.find((c) => c.isoCode === value);
  if (country) return country;
  country = countries.find((c) => c.name.toLowerCase() === value.toLowerCase());
  if (country) return country;
  return { name: value, isoCode: value, isCustom: true };
};

const findProvinceOption = (value, provinces) => {
  if (!value) return null;
  const province = provinces.find((p) => p.name === value);
  if (province) return province;
  return { name: value, isoCode: value, isCustom: true };
};

const InvoiceDetailsComp = () => {
  const { id } = useParams();
  const { userInfo } = useSelector((state) => state.userSignIn);
  const { documentType, invoice, pdf, loading, error, stripePaymentEnabled } =
    useSelector((state) => state.customInvoiceById);

  const { showSnackbar, SnackbarComponent } = useSnackbarNotification();
  const [editableInvoice, setEditableInvoice] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [currentEstimateId, setCurrentEstimateId] = useState(null);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const [emailLoader, setEmailLoader] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const isDisabled =
    documentType === "Estimate" && invoice?.status === "Converted";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const countries = useMemo(() => getCountries(), []);

  const countryValue = useMemo(() => {
    if (editableInvoice?.buyer?.address?.country) {
      return findCountryOption(editableInvoice.buyer.address.country, countries);
    }
    return null;
  }, [editableInvoice?.buyer?.address?.country, countries]);

  const countryIsoCode = useMemo(() => {
    return countryValue && !countryValue.isCustom ? countryValue.isoCode : "";
  }, [countryValue]);

  const provinces = useMemo(() => {
    return countryIsoCode ? getProvincesByCountry(countryIsoCode) : [];
  }, [countryIsoCode]);

  const provinceValue = useMemo(() => {
    if (editableInvoice?.buyer?.address?.province) {
      return findProvinceOption(editableInvoice.buyer.address.province, provinces);
    }
    return null;
  }, [editableInvoice?.buyer?.address?.province, provinces]);

  useEffect(() => {
    if (userInfo?.token) {
      dispatch(fetchInvoiceData(id, userInfo.token));
    }
  }, [dispatch, id, userInfo?.token]);

  useEffect(() => {
    if (invoice) {
      setEditableInvoice(invoice);
      setOriginalInvoice(invoice);
    }
  }, [invoice]);

  const handleInputChange = (field, value) => {
    let updatedInvoice = {
      ...editableInvoice,
      [field]: value,
    };

    if (field === "buyer") {
      updatedInvoice.buyer = {
        ...editableInvoice.buyer,
        ...value,
      };
      if (value.address && value.address.country && !value.address.province) {
        updatedInvoice.buyer.address = {
          ...updatedInvoice.buyer.address,
          province: "",
        };
      }
    }

    setEditableInvoice(updatedInvoice);
    setIsModified(
      JSON.stringify(updatedInvoice) !== JSON.stringify(originalInvoice)
    );
  };

  const handleDownloadPDF = () => {
    if (pdf) {
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${pdf}`;
      link.download = `Invoice_${invoice.invoiceNumber}.pdf`;
      link.click();
    }
  };

  const handlePrintPDF = () => {
    if (pdf) {
      const pdfWindow = window.open("");
      pdfWindow.document.write(
        `<iframe width='100%' height='100%' src='data:application/pdf;base64,${pdf}'></iframe>`
      );
      pdfWindow.document.close();
    }
  };

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true);
    setAnchorEl(null);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handlePostDelete = () => {
    handleCloseDeleteModal();
  };

  const handleSendInvoice = async () => {
    setEmailLoader(true);
    setIsSuccessModalOpen(true);
    try {
      const response = await axios.post(
        `${API_URI}/api/invoices/invoices/${id}/send`,
        {
          clientEmail: invoice.buyer.contact.email,
        },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      if (response.data.success) {
        showSnackbar(
          `Invoice successfully sent to ${invoice.buyer.contact.email}`,
          "success"
        );
        setIsSuccessModalOpen(true);
      } else {
        showSnackbar(
          "Failed to send the invoice: " + response.data.message,
          "error"
        );
      }
    } catch (error) {
      showSnackbar("Error sending invoice: " + error.message, "error");
    } finally {
      setEmailLoader(false);
    }
  };

  const handleSaveInvoice = async (status = null) => {
    try {
      const countryOption = findCountryOption(
        editableInvoice.buyer.address.country,
        countries
      );
      const updateData = {
        ...editableInvoice,
        ...(status && { status }), 
        buyer: {
          ...editableInvoice.buyer,
          address: {
            ...editableInvoice.buyer.address,
            country: countryOption.isCustom
              ? countryOption.name
              : countries.find((c) => c.isoCode === countryOption.isoCode)?.name ||
                countryOption.name,
          },
        },
      };
      const response = await axios.put(
        `${API_URI}/api/invoices/invoice-update/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      if (response.data.success) {
        const actionText = status === "Issued" ? "saved as Issued" : "saved successfully";
        showSnackbar(`Invoice ${actionText}!`, "success");
        setIsModified(false);
        dispatch(fetchInvoiceData(id, userInfo.token));
      } else {
        showSnackbar("Failed to save the invoice.", "error");
      }
    } catch (error) {
      showSnackbar("Error saving invoice: " + error.message, "error");
    }
  };

  const handleSave = async () => {
    await handleSaveInvoice("Issued");
  };

  const handleSaveAsDraft = async () => {
    await handleSaveInvoice("Draft");
  };

  const handleSaveAsPaid = async (modeOfPayment, paymentAmount) => {
    setLoadingModal(true);
    try {
      const response = await axios.post(
        `${API_URI}/api/invoices/update-invoice-status`,
        {
          id: [id],
          modeOfPayment,
          paymentAmount,
        },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      if (response.data.success) {
        setTimeout(() => {
          setLoadingModal(false);
          setOpenModal(false);
          dispatch(fetchInvoiceData(id, userInfo.token));
        }, 2000);
      } else {
        showSnackbar("Failed to save payment: " + response.data.message, "error");
        setLoadingModal(false);
      }
    } catch (error) {
      showSnackbar("Error saving payment: " + (error.response?.data?.message || error.message), "error");
      setLoadingModal(false);
    }
  };

  // Updated totalAmount to use remainingBalance
  const totalAmount = invoice ? parseFloat(invoice.remainingBalance) || 0 : 0;

  const handleOpenConvertModal = (estimateId) => {
    setCurrentEstimateId(estimateId);
    setIsConvertModalOpen(true);
    setAnchorEl(null);
  };

  const handleCloseConvertModal = () => {
    setIsConvertModalOpen(false);
    setCurrentEstimateId(null);
  };

  const handleConfirmConvert = async () => {
    try {
      const response = await axios.post(
        `${API_URI}/api/invoices/${invoice._id}/convert`,
        {},
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      const invoiceId = response.data.invoice._id;
      showSnackbar("Estimate successfully converted to an invoice!", "success");
      setTimeout(() => {
        navigate(`/invoice/${invoiceId}`);
      }, 200);
      handleCloseConvertModal();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while converting the estimate.",
        "error"
      );
    }
  };

  const handleCancel = () => {
    setEditableInvoice(originalInvoice);
    setIsModified(false);
  };

  const onDelete = (orderId) => {
    const updatedItems = editableInvoice.items.filter(
      (item) => item.orderId !== orderId
    );
    const newSubTotal = updatedItems.reduce(
      (acc, item) => acc + parseFloat(item.total || 0),
      0
    );
    const newDetailedTaxes = editableInvoice.detailedTaxes.map((tax) => {
      const newTaxValue = (newSubTotal * tax.percentage) / 100;
      return {
        ...tax,
        value: newTaxValue.toFixed(2),
      };
    });
    const newTotal =
      newSubTotal +
      newDetailedTaxes.reduce(
        (acc, tax) => acc + parseFloat(tax.value || 0),
        0
      );
    const updatedInvoice = {
      ...editableInvoice,
      items: updatedItems,
      subTotal: newSubTotal.toFixed(2),
      detailedTaxes: newDetailedTaxes,
      total: newTotal.toFixed(2),
    };
    setEditableInvoice(updatedInvoice);
    setIsModified(
      JSON.stringify(updatedInvoice) !== JSON.stringify(originalInvoice)
    );
  };

  if (loading || deleteLoading) {
    return <Loader />;
  }
  if (error) {
    return (
      <Alert severity="error">
        The Invoice is Removed and could not be found
        <Link
          to="/app/list-invoices"
          style={{ textDecoration: "underline", color: "blue" }}
        >
          Go to Invoice List
        </Link>
      </Alert>
    );
  }

  return (
    <div className="container-fluid p-4">
      <Card elevation={3} className="mb-4">
        <CardContent>
          <InvoiceHeader
            documentType={documentType}
            invoice={invoice}
            isDisabled={isDisabled}
            setOpenModal={setOpenModal}
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenConvertModal={handleOpenConvertModal}
            navigate={navigate}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Typography variant="h5" gutterBottom>
                  {documentType === "Estimate" ? "Estimate Details" : "Invoice Details"}
                </Typography>
                {isModified && (
                  <div className="d-flex align-items-center">
                    <Button
                      onClick={handleSave}
                      color="primary"
                      variant="contained"
                      className="me-2"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      color="secondary"
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <Divider className="mb-3" />
              {editableInvoice && (
                <>
                  <BuyerInfoForm
                    editableInvoice={editableInvoice}
                    handleInputChange={handleInputChange}
                    isDisabled={isDisabled}
                    stripePaymentEnabled={stripePaymentEnabled}
                    documentType={documentType}
                    countries={countries}
                    countryValue={countryValue}
                    provinces={provinces}
                    provinceValue={provinceValue}
                  />
                  <InvoiceDetailsForm
                    editableInvoice={editableInvoice}
                    handleInputChange={handleInputChange}
                    isDisabled={isDisabled}
                    documentType={documentType}
                  />
                  <ActionButtons
                    handleDownloadPDF={handleDownloadPDF}
                    handlePrintPDF={handlePrintPDF}
                    handleSendInvoice={handleSendInvoice}
                    handleSaveInvoice={handleSave}
                    handleSaveAsDraft={handleSaveAsDraft}
                    editableInvoice={editableInvoice}
                    isDisabled={isDisabled}
                    documentType={documentType}
                  />
                </>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <InvoicePreview pdf={pdf} documentType={documentType} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {editableInvoice && (
        <div>
          <InvoiceOrderTable invoice={editableInvoice} onDelete={onDelete} />
        </div>
      )}
      {/* Warning for missing remainingBalance */}
      {(!invoice?.remainingBalance && invoice?.remainingBalance !== 0) && (
        <Typography variant="body1" color="warning.main" sx={{ mb: 2 }}>
          Warning: Remaining balance data is missing or undefined. Displaying total amount instead.
        </Typography>
      )}
      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handlePostDelete}
        invoiceId={id}
        token={userInfo.token}
        showSnackbar={showSnackbar}
        setDeleteLoading={setDeleteLoading}
      />
      <InvoiceConvConfirmModal
        open={isConvertModalOpen}
        onClose={handleCloseConvertModal}
        onConfirm={handleConfirmConvert}
        title="Convert to Invoice"
        description="Are you sure you want to convert this estimate to an invoice? This action cannot be undone."
      />
      <SaveAsPaidModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveAsPaid}
        loading={loadingModal}
        totalAmount={totalAmount}
        invoiceCount={1}
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        handleClose={() => setIsSuccessModalOpen(false)}
        invoice={invoice}
        emailLoader={emailLoader}
      />
      <SnackbarComponent />
    </div>
  );
};

export default InvoiceDetailsComp;